 /**
  * Program Branding Names
  * Each category has a unique program name for marketing purposes
  */
 
 export interface ProgramBranding {
   name: string;
   tagline: string;
   fullName: string;
  icon: string;
  emoji: string;
  logoUrl?: string;
 }
 
 export const PROGRAM_NAMES: Record<string, ProgramBranding> = {
   // Professionals
   trainer: {
     name: "Rendix",
     tagline: "Para Entrenadores Personales",
     fullName: "Rendix - Plataforma para Entrenadores Personales",
    icon: "Dumbbell",
    emoji: "🏋️",
    logoUrl: "/logos/rendix.png",
   },
   physiotherapist: {
     name: "FisioAxis",
     tagline: "Para Fisioterapeutas",
     fullName: "FisioAxis - Plataforma para Fisioterapeutas",
    icon: "HeartPulse",
    emoji: "💆",
   logoUrl: "/logos/fisioaxis.png",
   },
   nutritionist: {
     name: "NutriCore",
     tagline: "Para Nutricionistas",
     fullName: "NutriCore - Plataforma para Nutricionistas",
    icon: "Apple",
    emoji: "🥗",
   logoUrl: "/logos/nutricore.png",
   },
   
   // Businesses
   yoga: {
     name: "Asanex",
     tagline: "Para Estudios de Yoga",
     fullName: "Asanex - Plataforma para Estudios de Yoga",
    icon: "Flower2",
    emoji: "🧘",
   logoUrl: "/logos/asanex.png",
   },
   crossfit: {
     name: "Crossnity",
     tagline: "Para Boxes de CrossFit",
     fullName: "Crossnity - Plataforma para Boxes de CrossFit",
    icon: "Flame",
    emoji: "💪",
   logoUrl: "/logos/crossnity.png",
   },
   gym: {
     name: "Gymnex",
     tagline: "Para Gimnasios",
     fullName: "Gymnex - Plataforma para Gimnasios",
    icon: "Building2",
    emoji: "🏢",
   // Cache-busted filename to avoid stale logo in browsers/service worker
   logoUrl: "/logos/gymnex-20260205.png",
   },
   pilates: {
     name: "Corevia",
     tagline: "Para Centros de Pilates",
     fullName: "Corevia - Plataforma para Centros de Pilates",
    icon: "Target",
    emoji: "🤸",
    logoUrl: "/logos/corevia.png",
   },
   dance: {
     name: "CoreoHub",
     tagline: "Para Academias de Baile",
     fullName: "CoreoHub - Plataforma para Academias de Baile",
    icon: "Music",
    emoji: "💃",
    logoUrl: "/logos/coreohub.png",
   },
   martial_arts: {
     name: "Budotrax",
     tagline: "Para Dojos de Artes Marciales",
     fullName: "Budotrax - Plataforma para Dojos de Artes Marciales",
    icon: "Swords",
    emoji: "🥋",
    logoUrl: "/logos/budotrax.png",
   },
 };
 
 export const getProgramName = (category: string): string => {
   return PROGRAM_NAMES[category]?.name || category;
 };
 
 export const getProgramBranding = (category: string): ProgramBranding | undefined => {
   return PROGRAM_NAMES[category];
 };

/**
 * Maps article/video categories to program names
 */
export const getProgramIcon = (category: string): string => {
  return PROGRAM_NAMES[category]?.icon || "Target";
};

export const getProgramEmoji = (category: string): string => {
  return PROGRAM_NAMES[category]?.emoji || "🎯";
};

export const getProgramLogoUrl = (category: string): string | undefined => {
  return PROGRAM_NAMES[category]?.logoUrl;
};

export const getCategoryProgramName = (categoryName: string): string => {
  const normalized = categoryName?.toLowerCase() || '';
  
  if (normalized.includes('entrena') || normalized.includes('fitness') || normalized.includes('fuerza') || normalized.includes('cardio')) return 'Rendix';
  if (normalized.includes('nutri') || normalized.includes('diet') || normalized.includes('alimentación')) return 'NutriCore';
  if (normalized.includes('fisio') || normalized.includes('rehab') || normalized.includes('lesion')) return 'FisioAxis';
  if (normalized.includes('yoga') || normalized.includes('medit') || normalized.includes('mindfulness')) return 'Asanex';
  if (normalized.includes('cross') || normalized.includes('funcional') || normalized.includes('hiit')) return 'Crossnity';
  if (normalized.includes('gym') || normalized.includes('gimnasio') || normalized.includes('musculación')) return 'Gymnex';
  if (normalized.includes('pilates') || normalized.includes('core') || normalized.includes('postural')) return 'Corevia';
  if (normalized.includes('bail') || normalized.includes('dance') || normalized.includes('zumba')) return 'CoreoHub';
  if (normalized.includes('marcial') || normalized.includes('karate') || normalized.includes('judo') || normalized.includes('boxeo') || normalized.includes('mma')) return 'Budotrax';
  
  return 'Rendix'; // Default
};

/**
 * Maps category to funnel path
 */
export const getCategoryFunnelPath = (categoryName: string): string => {
  const normalized = categoryName?.toLowerCase() || '';
  
  if (normalized.includes('entrena') || normalized.includes('fitness') || normalized.includes('fuerza') || normalized.includes('cardio')) return '/funnel/entrenador';
  if (normalized.includes('nutri') || normalized.includes('diet') || normalized.includes('alimentación')) return '/funnel/nutricionista';
  if (normalized.includes('fisio') || normalized.includes('rehab') || normalized.includes('lesion')) return '/funnel/fisioterapeuta';
  if (normalized.includes('yoga') || normalized.includes('medit') || normalized.includes('mindfulness')) return '/funnel/yoga';
  if (normalized.includes('cross') || normalized.includes('funcional') || normalized.includes('hiit')) return '/funnel/crossfit';
  if (normalized.includes('gym') || normalized.includes('gimnasio') || normalized.includes('musculación')) return '/funnel/fitness';
  if (normalized.includes('pilates') || normalized.includes('core') || normalized.includes('postural')) return '/funnel/pilates';
  if (normalized.includes('bail') || normalized.includes('dance') || normalized.includes('zumba')) return '/funnel/baile';
  if (normalized.includes('marcial') || normalized.includes('karate') || normalized.includes('judo') || normalized.includes('boxeo') || normalized.includes('mma')) return '/funnel/artes-marciales';
  
  return '/become-trainer'; // Default
};

/**
 * Get program type from category name
 */
export const getCategoryProgramType = (categoryName: string): string => {
  const normalized = categoryName?.toLowerCase() || '';
  
  if (normalized.includes('entrena') || normalized.includes('fitness') || normalized.includes('fuerza') || normalized.includes('cardio')) return 'trainer';
  if (normalized.includes('nutri') || normalized.includes('diet') || normalized.includes('alimentación')) return 'nutritionist';
  if (normalized.includes('fisio') || normalized.includes('rehab') || normalized.includes('lesion')) return 'physiotherapist';
  if (normalized.includes('yoga') || normalized.includes('medit') || normalized.includes('mindfulness')) return 'yoga';
  if (normalized.includes('cross') || normalized.includes('funcional') || normalized.includes('hiit')) return 'crossfit';
  if (normalized.includes('gym') || normalized.includes('gimnasio') || normalized.includes('musculación')) return 'gym';
  if (normalized.includes('pilates') || normalized.includes('core') || normalized.includes('postural')) return 'pilates';
  if (normalized.includes('bail') || normalized.includes('dance') || normalized.includes('zumba')) return 'dance';
  if (normalized.includes('marcial') || normalized.includes('karate') || normalized.includes('judo') || normalized.includes('boxeo') || normalized.includes('mma')) return 'martial_arts';
  
  return 'trainer'; // Default
};